<template>
	<div class="p-col-12 p-grid p-mr-0 p-ml-0 p-pb-0 p-pt-3">
		<div class="p-col-8 p-pl-0">
			<h1 class="headline p-component p-m-0">{{$t('Squeeze.UserManagement.MyApiKeys')}}</h1>
		</div>
		<div class="p-col-4 p-as-center p-grid p-jc-end">
			<Button class="p-button p-button-rounded p-button-text" icon="mdi mdi-close" @click="clickSettingDialog" />
		</div>
	</div>
	<div class="p-col-12 scroll-content">
		<ApiKeyList
			:apiKeys="apiKeys"
			:loading="loading"
			@onEntrySelect="onEntrySelect"
			@onClickDelete="openDeleteDialog"
		/>
	</div>

	<DialogDelete :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry" />

	<!-- Dialog für Felder -->
	<EntryDialog
		:show="showDialog"
		@onClose="showDialog = false, isSettingInvalid = true, showValidationMessage = false"
		@onConfirm="createApiKey"
		:loading="loadingDialog"
		:headerText="headerText"
	>
		<template #content>
			<ApiKeyForm
				:apiKey="apiKey"
				@update="onUpdate"
				:showErrorMessage="showValidationMessage"
			/>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {defineComponent, onMounted, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import {ApiKey, BatchClass} from "@dex/squeeze-client-ts";
import DialogDelete from "@/components/DialogDelete.vue";
import ApiKeyList from "@/components/settingDialog/apiKey/ApiKeyList.vue";
import ApiKeyForm from "@/components/settingDialog/apiKey/ApiKeyForm.vue";
import EntryDialog from "@/components/EntryDialog.vue";

export default defineComponent({
	name: "UserApiKeysView",
	components: {
		DialogDelete, ApiKeyList, ApiKeyForm, EntryDialog,
	},
	props: {
		userId: {
			type: Number,
			default: 0,
		},
	},
	emits: ['clickSettingDialog'],
	setup(props, { emit }) {
		const {t} = useI18n();
		const toast = useToast();

		/** Show loading in table? */
		const loading = ref(false);

		/** Service for getting the user-data */
		const userService = ClientManager.getInstance().squeeze.user;

		/** List of all users */
		const apiKeys = ref<ApiKey[]>([]);

		/** Currently active user */
		const apiKey = reactive<ApiKey>({
			name: '',
			value: '',
		})

		/** Show Delete-Dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Should the Entry-Dialog for Columns be shown? */
		const showDialog = ref<boolean>(false);

		/** Is the Form of the mails invalid? */
		const isValidationInvalid = ref(true);

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Show Loading on Save */
		const loadingDialog = ref(false);

		/** Text of the header in Entry-Dialog */
		const headerText = ref<string>(t('Squeeze.General.CreateEntry', { entryName: t('Squeeze.UserManagement.ApiKey') }));

		/** Triggered on update of attribute-form */
		const onUpdate = (data: BatchClass, valid: boolean) => {
			isValidationInvalid.value = valid;
			Object.assign(apiKey, data);
		}

		/** Reloads the table to get all users */
		const reloadData = () => {
			loading.value = true;
			userService.getUserApiKeys()
				.then(data => {
					apiKeys.value = data;
				})
				.catch(response => response.json().then ((err: any) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				}))
				.finally(() => {
					loading.value = false;
				})
		}

		onMounted(() =>{
			reloadData();
		})

		/**
		 * Triggered when an entry should be shown
		 * @param row
		 */
		const onEntrySelect = (row: ApiKey) => {
			Object.assign(apiKey, row);
			showDialog.value = true;
		}

		/**
		 * Opens the Delete Dialog
		 * @param row Row to delete
		 */
		const openDeleteDialog = (row: ApiKey) => {
			deleteDialog.value = true;
			Object.assign(apiKey, row);
		}

		/** Deletes a user */
		const deleteEntry = () => {
			if (apiKey && apiKey.value) {
				loading.value = true;
				userService.deleteUserApiKey(apiKey.value).then(() => {
					reloadData();
				}).catch(response => response.json().then ((err: any) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
			}
		}
		/** Deletes a user */
		const createApiKey = () => {
			if (isValidationInvalid.value) {
				showValidationMessage.value = true;
				return;
			}
			showValidationMessage.value = false;

			if (apiKey && apiKey.name) {
				loading.value = true;
				userService.addUserApiKey(apiKey).then(() => {
					showDialog.value = false;
					reloadData();
				}).catch(response => response.json().then ((err: any) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
			}
		}

		/** Emit of click on close button */
		const clickSettingDialog = () => {
			emit('clickSettingDialog');
		}

		return {loading, apiKeys, apiKey, deleteDialog, userService,
			showDialog, isValidationInvalid, showValidationMessage,
			loadingDialog, headerText, createApiKey, onEntrySelect,
			deleteEntry, openDeleteDialog, onUpdate, clickSettingDialog};
	},
});

</script>

<style scoped>

.headline {
	font-size: 2rem;
	color: var(--dex-primary-color);
	margin-bottom: 1rem;
}

.scroll-content {
	height: calc(50vh - 4.5rem);
	min-height: 15rem;
	margin: 0;
	padding-bottom: 0;
}

</style>
